import { useGetFrontRichiesteIdAllegati } from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { type RichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTO.ts';
import { type RichiestaModuloDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaModuloDTO.ts';
import { LoaderSpin } from '@framework/ui/components/loader/LoaderSpin.tsx';
import { VerticalSpace } from '@framework/ui/components/space/VerticalSpace.tsx';
import { SectionTitle } from '@framework/ui/components/summary/SectionTitle.tsx';
import { SummaryDetailCard } from '@framework/ui/components/summary/SummaryDetailCard.tsx';
import { TableData } from '@framework/ui/components/table/TableData.tsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TbPaperclip } from 'react-icons/tb';
import {
  buildAttachmentRow,
  getAttachmentsColumns,
} from './attachmentsColumns.tsx';
import {
  isOmissisAttachment,
  sortAttachmentsWithOmissis,
} from './sortAttachmentsWithOmissis.ts';

interface IExtendedAttachmentsTableProps {
  submissionId: string;
  modules: RichiestaModuloDTO[];
  submission?: RichiestaDTO;
}

/**
 * Tabella allegati nel riepilogo esteso.
 * Recupera gli allegati dall'API e li mostra con paginazione e ricerca locale per colonna.
 * Per ogni allegato con versione omissis, aggiunge una riga aggiuntiva.
 */
export function ExtendedAttachmentsTable(
  props: IExtendedAttachmentsTableProps,
) {
  const { submissionId, modules, submission } = props;
  const { t } = useTranslation();

  const { data, isLoading } = useGetFrontRichiesteIdAllegati(submissionId, {
    query: { keepPreviousData: true, enabled: Boolean(submissionId) },
  });

  const dataSource = useMemo(() => {
    if (!data) return [];

    return sortAttachmentsWithOmissis(data).map(attachment =>
      buildAttachmentRow(attachment, isOmissisAttachment(attachment), modules),
    );
  }, [data, modules]);

  const columns = useMemo(
    () => getAttachmentsColumns(submissionId, modules, submission),
    [submissionId, modules, submission],
  );

  if (isLoading) {
    return <LoaderSpin transparent />;
  }

  if (!data || data.length === 0) {
    return null;
  }

  return (
    <div>
      <SectionTitle
        icon={<TbPaperclip />}
        title={t('submission-summary.elenco-allegati', 'Elenco degli Allegati')}
      />
      <VerticalSpace />

      <SummaryDetailCard noPadding>
        <TableData
          removeShadow
          dataSource={dataSource}
          columns={columns}
          rowKey="key"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: [5, 10, 20],
            hideOnSinglePage: true,
          }}
          size="middle"
        />
      </SummaryDetailCard>
    </div>
  );
}
